import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { downloadOverdueExport, getUsers } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Download, FileDown, RefreshCcw, Search } from 'lucide-react';
import { OverdueRow } from './OverdueRow';
import { SnoozeDialog } from './SnoozeDialog';
import { ReassignDialog } from './ReassignDialog';
import { NoteDialog } from '../dossier/NoteDialog';
import { fuzzyMatch } from './shared';

/**
 * Generic overdue list tab. Fetches rows via loader, handles filters, search, exports and actions.
 * Props:
 *   loader: async () => Array
 *   emptyMsg: string
 *   snoozeEntityType: "dossier" | "relance" | "prospect"
 *   showLastContact: bool (for clients silencieux)
 *   showDateField: key in row to render in date col (e.g. "date_rdv")
 *   columnLabel: label above date col
 *   exportCategory: slug for /api/exports/overdue
 *   reassignEnabled: bool (true for dossier rows)
 */
export const OverdueTab = ({
    loader,
    emptyMsg,
    snoozeEntityType,
    showLastContact = false,
    showDateField = null,
    columnLabel = null,
    exportCategory,
    reassignEnabled = true,
}) => {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin' || user?.role === 'manager';
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [commercialFilter, setCommercialFilter] = useState('all');
    const [users, setUsersList] = useState([]);

    const [snoozeEntity, setSnoozeEntity] = useState(null);
    const [reassignEntity, setReassignEntity] = useState(null);
    const [noteDossierId, setNoteDossierId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await loader();
            setRows(res.data || []);
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => {
        if (!isAdmin) return;
        getUsers().then((r) => setUsersList(r.data || [])).catch(() => {});
    }, [isAdmin]);

    const filtered = useMemo(() => {
        return rows.filter((r) => {
            if (commercialFilter !== 'all' && r.commercial_id !== commercialFilter) return false;
            if (search) {
                const blob = [r.titre, r.dossier_titre, r.prospect_nom, r.prospect_telephone, r.prospect_email, r.commercial_nom].filter(Boolean).join(' ');
                if (!fuzzyMatch(blob, search)) return false;
            }
            return true;
        });
    }, [rows, search, commercialFilter]);

    return (
        <div className="bg-white border border-[#E4E4E7] rounded-sm" data-testid={`overdue-tab-${exportCategory}`}>
            <div className="p-4 border-b border-[#E4E4E7] flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                    <Input
                        placeholder="Rechercher..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 h-10 rounded-sm"
                        data-testid="overdue-search-input"
                    />
                </div>
                {isAdmin && users.length > 0 && (
                    <Select value={commercialFilter} onValueChange={setCommercialFilter}>
                        <SelectTrigger className="h-10 rounded-sm w-56" data-testid="overdue-commercial-filter">
                            <SelectValue placeholder="Tous les commerciaux" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les commerciaux</SelectItem>
                            {users.filter((u) => u.role === 'commercial' || u.role === 'manager').map((u) => (
                                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                <Button
                    variant="outline"
                    onClick={load}
                    className="rounded-sm h-10"
                    data-testid="overdue-refresh-btn"
                >
                    <RefreshCcw className="w-4 h-4" />
                </Button>
                {isAdmin && (
                    <>
                        <Button
                            variant="outline"
                            onClick={() => downloadOverdueExport(exportCategory, 'csv')}
                            className="rounded-sm h-10"
                            data-testid="overdue-export-csv-btn"
                        >
                            <Download className="w-4 h-4 mr-1" /> CSV
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => downloadOverdueExport(exportCategory, 'xlsx')}
                            className="rounded-sm h-10"
                            data-testid="overdue-export-xlsx-btn"
                        >
                            <FileDown className="w-4 h-4 mr-1" /> XLSX
                        </Button>
                    </>
                )}
            </div>

            <div className="grid grid-cols-12 gap-3 px-3 py-2 bg-[#FAFAFA] text-xs text-[#52525B] uppercase font-semibold border-b border-[#E4E4E7]">
                <div className="col-span-4">Dossier / Client</div>
                <div className="col-span-2">Commercial</div>
                <div className="col-span-2">{showLastContact ? 'Dernier contact' : (columnLabel || 'Date')}</div>
                <div className="col-span-1">Retard</div>
                <div className="col-span-3 text-right">Actions</div>
            </div>

            {loading ? (
                <div className="p-8 text-center">
                    <div className="w-6 h-6 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-[#52525B] text-sm italic">{emptyMsg}</div>
            ) : (
                <div className="divide-y divide-[#E4E4E7]">
                    {filtered.map((row) => (
                        <OverdueRow
                            key={row.id || row.prospect_id}
                            row={row}
                            isAdmin={isAdmin}
                            showLastContact={showLastContact}
                            showDateField={showDateField}
                            columnLabel={columnLabel}
                            onSnooze={() => setSnoozeEntity({ type: snoozeEntityType, id: snoozeEntityType === 'prospect' ? row.prospect_id : (snoozeEntityType === 'relance' ? row.id : row.id) })}
                            onReassign={reassignEnabled && (row.overdue_type !== 'client_silencieux' && row.overdue_type !== 'relance_en_retard') ? () => setReassignEntity({ id: row.id, current: row.commercial_id }) : null}
                            onOpenNote={row.overdue_type === 'relance_en_retard' ? () => setNoteDossierId(row.dossier_id_str) : (row.id && row.overdue_type !== 'client_silencieux' ? () => setNoteDossierId(row.id) : null)}
                        />
                    ))}
                </div>
            )}

            <SnoozeDialog
                open={!!snoozeEntity}
                onOpenChange={(o) => !o && setSnoozeEntity(null)}
                entity={snoozeEntity}
                onDone={load}
            />
            {reassignEnabled && (
                <ReassignDialog
                    open={!!reassignEntity}
                    onOpenChange={(o) => !o && setReassignEntity(null)}
                    dossierId={reassignEntity?.id}
                    currentCommercialId={reassignEntity?.current}
                    onDone={load}
                />
            )}
            <NoteDialog
                open={!!noteDossierId}
                onOpenChange={(o) => !o && setNoteDossierId(null)}
                dossierId={noteDossierId}
                onAdded={load}
            />
        </div>
    );
};
